import React, { useState, useRef, useEffect } from 'react';
import {
  BiLogoJavascript, BiLogoReact, BiLogoNodejs,
  BiLogoTypescript, BiLogoHtml5, BiLogoCss3,
  BiLogoGoLang
} from 'react-icons/bi';
import { SiExpo, SiMysql } from 'react-icons/si';
import { FaAws } from 'react-icons/fa';
import { ReactComponent as OdooIcon } from '../assets/icons/odoo.svg';

const TECH = {
  JAVASCRIPT:   { label: 'JavaScript',   icon: BiLogoJavascript, color: '#F7DF1E', isReactIcon: true  },
  TYPESCRIPT:   { label: 'TypeScript',   icon: BiLogoTypescript, color: '#3178C6', isReactIcon: true  },
  REACT:        { label: 'React',        icon: BiLogoReact,      color: '#61DAFB', isReactIcon: true  },
  REACT_NATIVE: { label: 'React Native', icon: BiLogoReact,      color: '#61DAFB', isReactIcon: true  },
  NODE:         { label: 'Node.js',      icon: BiLogoNodejs,     color: '#68A063', isReactIcon: true  },
  AWS:          { label: 'AWS',          icon: FaAws,            color: '#FF9900', isReactIcon: true  },
  SQL:          { label: 'SQL',          icon: SiMysql,          color: '#4479A1', isReactIcon: true  },
  ODOO:         { label: 'Odoo',         icon: OdooIcon,         color: '#714B67', isReactIcon: false },
  HTML:         { label: 'HTML',         icon: BiLogoHtml5,      color: '#E34F26', isReactIcon: true  },
  CSS:          { label: 'CSS',          icon: BiLogoCss3,       color: '#1572B6', isReactIcon: true  },
  EXPO:         { label: 'Expo',         icon: SiExpo,           color: '#ffffff', isReactIcon: true  },
  GOLANG:       { label: 'Go',           icon: BiLogoGoLang,     color: '#00A7D0', isReactIcon: true  },
};

function useInView(threshold = 0.15) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

function TechBadge({ techKey, size = 'sm', delay = 0, visible = true }) {
  const [hovered, setHovered] = useState(false);
  const tech = TECH[techKey];
  if (!tech) return null;
  const Icon = tech.icon;
  const iconSize = size === 'sm' ? 13 : 16;

  return (
    <div
      title={tech.label}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: size === 'sm' ? 5 : 6,
        padding: size === 'sm' ? '4px 10px' : '6px 12px',
        borderRadius: 8,
        border: `1px solid ${hovered ? tech.color : 'rgba(255,255,255,0.08)'}`,
        backgroundColor: hovered ? `${tech.color}12` : 'rgba(255,255,255,0.03)',
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0) scale(1)' : 'translateY(6px)',
        transition: `opacity 0.35s ease ${delay}ms, transform 0.35s ease ${delay}ms, border-color 0.2s ease, background-color 0.2s ease, box-shadow 0.2s ease`,
        boxShadow: hovered ? `0 2px 12px ${tech.color}25` : 'none',
        cursor: 'default',
        flexShrink: 0,
      }}
    >
      {tech.isReactIcon ? (
        <Icon style={{ color: hovered ? tech.color : 'rgba(255,255,255,0.45)', fontSize: iconSize, flexShrink: 0, transition: 'color 0.2s' }} />
      ) : (
        <Icon width={iconSize} height={iconSize} style={{ color: hovered ? tech.color : 'rgba(255,255,255,0.45)', flexShrink: 0, transition: 'color 0.2s' }} />
      )}
      <span style={{
        fontSize: size === 'sm' ? 11 : 12,
        color: hovered ? '#fff' : 'rgba(255,255,255,0.5)',
        fontFamily: 'Manrope, sans-serif',
        transition: 'color 0.2s',
        whiteSpace: 'nowrap',
      }}>
        {tech.label}
      </span>
    </div>
  );
}

const ExperienceItem = ({ title, company, companyUrl, description, date, badge, activities, tags, index, isLast, labels }) => {
  const [expanded, setExpanded] = useState(false);
  const [cardHovered, setCardHovered] = useState(false);
  const [ref, inView] = useInView(0.1);

  return (
    <div
      ref={ref}
      style={{
        opacity: inView ? 1 : 0,
        transform: inView ? 'translateX(0)' : 'translateX(-24px)',
        transition: `opacity 0.55s ease ${index * 120}ms, transform 0.55s ease ${index * 120}ms`,
      }}
    >
      <div className="flex gap-4 sm:gap-6">

        {/* Línea de tiempo */}
        <div className="flex flex-col items-center" style={{ width: 20, flexShrink: 0 }}>
          <div
            style={{
              width: 12,
              height: 12,
              borderRadius: '50%',
              border: `2px solid ${inView ? '#eab308' : 'rgba(255,255,255,0.1)'}`,
              backgroundColor: expanded ? '#eab308' : 'black',
              flexShrink: 0,
              marginTop: 22,
              transition: 'background-color 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease',
              boxShadow: expanded ? '0 0 12px rgba(234,179,8,0.5)' : 'none',
            }}
          />
          {!isLast && (
            <div
              style={{
                flex: 1,
                width: 1,
                backgroundColor: 'rgba(255,255,255,0.06)',
                marginTop: 6,
                minHeight: 32,
              }}
            />
          )}
        </div>

        {/* Card */}
        <div
          className="flex-1 mb-6"
          onMouseEnter={() => setCardHovered(true)}
          onMouseLeave={() => setCardHovered(false)}
          style={{
            borderRadius: 16,
            border: `1px solid ${expanded ? 'rgba(234,179,8,0.15)' : cardHovered ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.05)'}`,
            backgroundColor: expanded ? 'rgba(234,179,8,0.03)' : cardHovered ? 'rgba(255,255,255,0.02)' : 'transparent',
            transition: 'border-color 0.25s ease, background-color 0.25s ease',
            overflow: 'hidden',
          }}
        >
          <div className="p-5 sm:p-6">
            <div className="flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-5">

              {/* Fecha + badge */}
              <div className="sm:w-[120px] lg:w-[140px] shrink-0 flex sm:flex-col gap-2 sm:gap-1.5 pt-0.5">
                <time style={{
                  fontSize: 13,
                  color: 'rgba(255,255,255,0.35)',
                  fontFamily: 'monospace',
                  whiteSpace: 'nowrap',
                }}>
                  {date}
                </time>
                {badge && (
                  <span style={{
                    fontSize: 11,
                    fontWeight: 600,
                    padding: '2px 8px',
                    borderRadius: 999,
                    width: 'fit-content',
                    backgroundColor: 'rgba(234,179,8,0.1)',
                    color: '#eab308',
                    border: '1px solid rgba(234,179,8,0.2)',
                    fontFamily: 'Manrope, sans-serif',
                    whiteSpace: 'nowrap',
                  }}>
                    {badge}
                  </span>
                )}
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 mb-2">
                  <h3 style={{
                    fontSize: 16,
                    fontWeight: 600,
                    color: '#fff',
                    margin: 0,
                    fontFamily: 'Manrope, sans-serif',
                  }}>
                    {title}
                  </h3>
                  <span style={{ color: 'rgba(255,255,255,0.15)', fontSize: 14 }} className="hidden sm:inline">·</span>
                  <a
                    href={companyUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      fontSize: 13,
                      color: 'rgba(255,255,255,0.35)',
                      textDecoration: 'none',
                      transition: 'color 0.2s',
                      fontFamily: 'Manrope, sans-serif',
                    }}
                    onMouseEnter={e => e.target.style.color = '#eab308'}
                    onMouseLeave={e => e.target.style.color = 'rgba(255,255,255,0.35)'}
                  >
                    {company}
                  </a>
                </div>

                <p style={{
                  fontSize: 13,
                  color: 'rgba(255,255,255,0.4)',
                  lineHeight: 1.7,
                  margin: 0,
                  marginBottom: 14,
                  fontFamily: 'Manrope, sans-serif',
                }}>
                  {description}
                </p>

                {!expanded && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {tags.map((key, i) => (
                      <TechBadge key={i} techKey={key} size="sm" delay={i * 30} visible={inView} />
                    ))}
                  </div>
                )}
              </div>

              {/* Toggle expand */}
              <button
                onClick={() => setExpanded(!expanded)}
                style={{
                  alignSelf: 'flex-start',
                  flexShrink: 0,
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  border: `1px solid ${expanded ? 'rgba(234,179,8,0.4)' : 'rgba(255,255,255,0.1)'}`,
                  backgroundColor: expanded ? 'rgba(234,179,8,0.08)' : 'transparent',
                  color: expanded ? '#eab308' : 'rgba(255,255,255,0.3)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  outline: 'none',
                }}
                onMouseEnter={e => {
                  if (!expanded) {
                    e.currentTarget.style.borderColor = 'rgba(255,255,255,0.3)';
                    e.currentTarget.style.color = '#fff';
                  }
                }}
                onMouseLeave={e => {
                  if (!expanded) {
                    e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)';
                    e.currentTarget.style.color = 'rgba(255,255,255,0.3)';
                  }
                }}
                aria-label={expanded ? labels.collapse : labels.expand}
              >
                <svg
                  style={{
                    width: 13,
                    height: 13,
                    transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.3s ease',
                  }}
                  fill="none" stroke="currentColor" viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

            </div>
          </div>

          {/* Expanded */}
          {expanded && (
            <div
              style={{
                borderTop: '1px solid rgba(255,255,255,0.05)',
                padding: '20px 24px 24px',
                paddingLeft: window.innerWidth >= 640 ? 'calc(24px + 140px)' : 24,
                animation: 'fadeSlideIn 0.3s ease',
              }}
            >
              <style>{`
                @keyframes fadeSlideIn {
                  from { opacity: 0; transform: translateY(-8px); }
                  to   { opacity: 1; transform: translateY(0); }
                }
              `}</style>

              <div style={{ marginBottom: 20 }}>
                <p style={{
                  fontSize: 10,
                  color: 'rgba(255,255,255,0.2)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.15em',
                  marginBottom: 12,
                  fontFamily: 'Manrope, sans-serif',
                }}>
                  {labels.responsibilities}
                </p>
                <ul style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                  gap: '6px 24px',
                  listStyle: 'none',
                  padding: 0,
                  margin: 0,
                }}>
                  {activities.map((act, i) => (
                    <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                      <span style={{
                        marginTop: 8,
                        flexShrink: 0,
                        width: 4,
                        height: 4,
                        borderRadius: '50%',
                        backgroundColor: 'rgba(234,179,8,0.4)',
                      }} />
                      <span style={{
                        fontSize: 13,
                        color: 'rgba(255,255,255,0.45)',
                        lineHeight: 1.7,
                        fontFamily: 'Manrope, sans-serif',
                      }}>
                        {act}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <p style={{
                  fontSize: 10,
                  color: 'rgba(255,255,255,0.2)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.15em',
                  marginBottom: 12,
                  fontFamily: 'Manrope, sans-serif',
                }}>
                  {labels.techStack}
                </p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {tags.map((key, i) => (
                    <TechBadge key={i} techKey={key} size="lg" delay={i * 40} visible={true} />
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExperienceItem;
